import React, { useState, useEffect } from 'react';
import { DonorProfileService } from '../../services/DonorProfileService';
import * as WechatAuthService from '../../services/WechatAuthService';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { TopBar } from '../../components/TopBar/TopBar';
import { useOrganization } from '../../contexts/OrganizationContext';
import './MyInfoView.css';

interface MyInfoViewProps {
  onBack: () => void;
  onLogoutClick: () => void;
}

/**
 * 我的信息页面（只读）
 */
export const MyInfoView: React.FC<MyInfoViewProps> = ({ onBack, onLogoutClick }) => {
  const { organization } = useOrganization();
  const [profile, setProfile] = useState({
    avatar: '',
    nickname: '',
    name: '',
    phone: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        showError('未获取到微信授权信息', 'MyInfoView.loadProfile');
        setLoading(false);
        return;
      }

      const userProfile = await DonorProfileService.getProfile(openId);
      if (userProfile) {
        setProfile({
          avatar: userProfile.avatar || '',
          nickname: userProfile.nickname || '',
          name: userProfile.name || '',
          phone: userProfile.phone || '',
        });
      } else {
        // 如果没有用户信息，尝试从微信获取昵称和头像
        const nickname = WechatAuthService.getWechatNickname();
        const avatar = WechatAuthService.getWechatAvatar();
        setProfile({
          avatar: avatar || '',
          nickname: nickname || '',
          name: '',
          phone: '',
        });
      }
    } catch (error) {
      logError('[MyInfoView] Failed to load profile', error);
      showError('加载用户信息失败', 'MyInfoView.loadProfile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="my-info-view">
        <TopBar title="我的信息" showBack onBack={onBack} />
        <div className="my-info-loading">加载中...</div>
      </div>
    );
  }

  return (
    <div className="my-info-view">
      <TopBar title="我的信息" showBack onBack={onBack} />
      
      <div className="my-info-content">
        <div className="my-info-section">
          <div className="my-info-item">
            <span className="my-info-label">头像</span>
            <div className="my-info-value">
              {profile.avatar ? (
                <img src={profile.avatar} alt="头像" className="my-info-avatar" />
              ) : (
                <div className="my-info-avatar-placeholder">无</div>
              )}
            </div>
          </div>
          
          <div className="my-info-item">
            <span className="my-info-label">昵称</span>
            <div className="my-info-value">
              <span>{profile.nickname || '未填写'}</span>
            </div>
          </div>
          
          <div className="my-info-item">
            <span className="my-info-label">姓名</span>
            <div className="my-info-value">
              <span>{profile.name || '未填写'}</span>
            </div>
          </div>
          
          <div className="my-info-item">
            <span className="my-info-label">绑定手机号</span>
            <div className="my-info-value">
              <span>{profile.phone || '未填写'}</span>
            </div>
          </div>
        </div>
        
        {/* 注销账号按钮 */}
        <div className="my-info-logout">
          <button 
            className="my-info-logout-btn" 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const logoutAgreement = organization?.logoutAgreement;
              if (!logoutAgreement || logoutAgreement.trim() === '') {
                showError('未上传注销协议，此功能不可用', 'MyInfoView');
                return;
              }
              logInfo('[MyInfoView] Logout button clicked, calling onLogoutClick');
              onLogoutClick();
            }}
          >
            注销账号
          </button>
        </div>
      </div>
    </div>
  );
};

